import { useCallback, useEffect, useRef, useState } from 'react';
import { IconLogout } from '@/components/Icons';

interface UserMenuProps {
  name: string;
  email?: string | null;
  /** Optional line above the name, e.g. the tenant a portal user belongs to. */
  context?: string | null;
  onSignOut: () => void;
  signingOut?: boolean;
}

/**
 * Account button for the top-right of the app bar — the position users expect
 * from every other admin tool, rather than tucked at the bottom of the sidebar.
 *
 * Shared by the SA console and the tenant portal so both behave identically.
 * The full email lives inside the panel: it is long, rarely needed at a glance,
 * and would otherwise squeeze the bar on small screens.
 */
export function UserMenu({ name, email, context, onSignOut, signingOut = false }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const initials = (name || email || '?').slice(0, 2).toUpperCase();

  const close = useCallback(() => setOpen(false), []);

  // Dismiss on an outside click or Escape, and return focus to the trigger so
  // keyboard users aren't dropped at the top of the document.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [close, open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 text-sm transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
      >
        <span
          aria-hidden="true"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600/[0.12] text-xs font-semibold text-brand-700"
        >
          {initials}
        </span>
        <span className="hidden max-w-[10rem] truncate font-medium text-slate-900 sm:block">{name}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          className={`hidden h-4 w-4 text-slate-500 transition-transform sm:block ${open ? 'rotate-180' : ''}`}
        >
          <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="sr-only">Account menu</span>
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 z-40 mt-2 w-64 animate-panel-in overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          <div className="border-b border-slate-100 px-4 py-3">
            {context ? <p className="truncate text-xs text-slate-500">{context}</p> : null}
            <p className="truncate text-sm font-semibold text-slate-900">{name}</p>
            {email ? (
              <p className="truncate text-xs text-slate-500" title={email}>
                {email}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onSignOut();
            }}
            disabled={signingOut}
            // Rose, matching `.btn-danger` — the app's single destructive
            // colour, rather than introducing a second red. 4.7:1 on white and
            // 5.7:1 on the hover tint, so it stays AA at body size.
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700 disabled:opacity-60 focus-visible:bg-rose-50 focus-visible:outline-none"
          >
            <IconLogout className="h-4 w-4" />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
